import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@material-ui/core";
import { useAuth } from "../auth/AuthContext";
import { connectEventbrite, eventbriteRequest, hasEventbriteIdentity, loadAllEventbritePages } from "./eventbriteService";
import { toAttendeeSpreadsheet } from "./attendeeData";

const EventbriteImportDialog = ({ open, onClose, onImport }) => {
  const { user } = useAuth();
  const signedInWithEventbrite = hasEventbriteIdentity(user);
  const [connected, setConnected] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [organizationId, setOrganizationId] = useState("");
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const requestRef = useRef(null);

  // Cancel and ignore obsolete responses when closing or changing organizations.
  const run = async (label, task) => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setBusy(label);
    setError("");
    try { await task(controller.signal); }
    catch (failure) { if (!controller.signal.aborted) setError(failure.message || "Could not import from Eventbrite."); }
    finally { if (!controller.signal.aborted) setBusy(""); }
  };
  const loadOrganizations = async (signal) => {
    const items = await loadAllEventbritePages({ action: "organizations" }, { signal });
    if (signal.aborted) return;
    setOrganizations(items);
    if (items.length === 1) {
      setOrganizationId(items[0].id);
      await loadEvents(items[0].id, signal);
    }
  };
  const loadEvents = async (id, signal) => {
    const items = await loadAllEventbritePages({ action: "events", organizationId: id }, { signal });
    if (!signal.aborted) setEvents(items.filter((event) => !event.isSeriesParent));
  };
  useEffect(() => {
    if (!open) return undefined;
    setOrganizations([]);
    setEvents([]);
    setOrganizationId("");
    setEventId("");
    setConnected(false);
    run("Checking connection…", async (signal) => {
      const status = await eventbriteRequest({ action: "status" }, signal);
      if (signal.aborted) return;
      setConnected(status.connected);
      if (status.connected) await loadOrganizations(signal);
    });
    return () => requestRef.current?.abort();
    // Open starts a fresh session; helpers use this session's AbortSignal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const connect = () => run("Connecting to Eventbrite…", async (signal) => {
    await connectEventbrite(signal);
    if (signal.aborted) return;
    setConnected(true);
    await loadOrganizations(signal);
  });
  const disconnect = () => run("Disconnecting…", async (signal) => {
    await eventbriteRequest({ action: "disconnect" }, signal);
    if (signal.aborted) return;
    setConnected(false);
    setOrganizations([]);
    setEvents([]);
    setOrganizationId("");
    setEventId("");
  });
  const importAttendees = () => run("Loading attendees…", async (signal) => {
    const attendees = await loadAllEventbritePages({ action: "attendees", eventId }, {
      signal, onProgress: (count) => { if (!signal.aborted) setBusy(`Loading attendees… ${count} received`); },
    });
    if (signal.aborted) return;
    const fileData = toAttendeeSpreadsheet(attendees);
    if (!fileData.data.length) throw new Error("This event has no active attendees to import. Choose another event or try again after people register.");
    onImport({ event: events.find((event) => event.id === eventId), fileData });
  });
  const close = () => { requestRef.current?.abort(); onClose(); };

  return <Dialog open={open} onClose={close} maxWidth="sm" fullWidth aria-labelledby="eventbrite-import-title">
    <DialogTitle id="eventbrite-import-title">Import from Eventbrite</DialogTitle>
    <DialogContent>
      <p>Choose an event to import attendee names, companies, ticket types, ticket QR codes and custom answers. Cancelled and refunded tickets are excluded.</p>
      <p>This imports a snapshot. You can review the fields before saving your attendees.</p>
      {error && <div className="event-name-error" role="alert">{error}</div>}
      {busy && <p role="status" aria-live="polite">{busy}</p>}
      {!connected && <button type="button" className="primary" disabled={Boolean(busy)} onClick={connect}>{signedInWithEventbrite ? "Reconnect Eventbrite" : "Connect Eventbrite"}</button>}
      {connected && <>
        <TextField select fullWidth margin="normal" variant="outlined" id="eventbrite-organization" label="Organization" value={organizationId}
          disabled={Boolean(busy)} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}
          onChange={(event) => {
            const id = event.target.value;
            setOrganizationId(id); setEventId(""); setEvents([]);
            if (id) run("Loading events…", (signal) => loadEvents(id, signal));
          }}>
          <option value="">Choose an organization</option>
          {organizations.map((org) => <option key={org.id} value={org.id}>{org.name}</option>)}
        </TextField>
        {!busy && !organizations.length && <p>No organizations were found. Connect an Eventbrite organizer account to import events.</p>}
        <TextField select fullWidth margin="normal" variant="outlined" id="eventbrite-event" label="Event" value={eventId}
          disabled={Boolean(busy) || !organizationId} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}
          onChange={(event) => setEventId(event.target.value)}>
          <option value="">Choose an event</option>
          {events.map((event) => <option key={event.id} value={event.id}>
            {event.name}{event.start ? ` — ${event.start.slice(0, 10)}` : ""}{event.status ? ` (${event.status})` : ""}
          </option>)}
        </TextField>
        {!busy && organizationId && !events.length && <p>No events were found for this organization.</p>}
        <button type="button" className="button" disabled={Boolean(busy)} onClick={disconnect}>Disconnect Eventbrite</button>
        <button type="button" className="button" disabled={Boolean(busy)} onClick={connect}>Reconnect</button>
      </>}
    </DialogContent>
    <DialogActions>
      <button type="button" className="button" onClick={close}>Cancel</button>
      {connected && <button type="button" className="primary" disabled={Boolean(busy) || !eventId} onClick={importAttendees}>Import attendees</button>}
    </DialogActions>
  </Dialog>;
};
export default EventbriteImportDialog;
